import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

import api from '../../../services/api';

import {
  DashboardContainer,
  HeaderTitle,
  DashboardChartBox,
  DashoboardFilterBox,
  DashboardFilterContent,
  ContainerEmpty,
  DashBoardChartsContent,
  SummaryBox,
  DashBoardRespondentStatistics,
  HelpIcon,
} from './styles';

interface Survey {
  surveyId: number;
  title: string;
}
interface TimingResponse {
  rawTimings: RawTiming[];
  metadata: {
    totalTime: number;
    totalRespondents: number;
    totalPages: number;
  };
}

interface RawTiming {
  pageNo: number;
  time: number;
  resultId: number;
}

interface PageTimingData {
  pageNo: number;
  averageTimeMs: number;
  totalResponses: number;
}

interface RespondentTimingData {
  resultId: number;
  totalTimeMs: number;
  pageCount: number;
}
interface State {
  project: {
    name: string;
    projectId: string;
  };
  idSurvey?: string;
}

const PageTimingAnalytics: React.FC = () => {
  const formRef = useRef<FormHandles>(null);
  const hasAutoSelectedSurvey = useRef(false);
  const { projectId } = useParams();
  const location = useLocation();
  const state = location.state as State;
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [surveyId, setSurveyId] = useState<number>(0);
  const [pageTimingData, setPageTimingData] = useState<PageTimingData[]>([]);
  const [respondentTimingData, setRespondentTimingData] = useState<
    RespondentTimingData[]
  >([]);
  const [surveyAverageTime, setSurveyAverageTime] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [timingResponse, setTimingResponse] = useState<TimingResponse | null>(
    null,
  );
  const [statistics, setStatistics] = useState<any[]>([]);
  const [dropOutData, setDropOutData] = useState<any[]>([]);
  const [dropOutResults, setDropOutResults] = useState<any[]>([]);
  const [totalResults, setTotalResults] = useState<number>();
  const [totalPagesInSurvey, setTotalPagesInSurvey] = useState<number>(0);
  const [
    maxPageNumberAnsweredFinishedSurvey,
    setMaxPageNumberAnsweredFinishedSurvey,
  ] = useState<number>(0);
  const [
    totalPeopleFinishedSurveyByLastPageNumber,
    setTotalPeopleFinishedSurveyByLastPageNumber,
  ] = useState<any[]>([]);
  const tooltips = [
    {
      statistic: 'Number of Links',
      text: 'Total number of survey links that have been generated.',
    },
    {
      statistic: 'Started',
      text: 'Participants who started the survey.',
    },
    {
      statistic: '≥ 30%',
      text: 'Participants who completed at least 30% of the survey.',
    },
    {
      statistic: '< 30%',
      text: 'Participants who completed less than 30% of the survey.',
    },
    {
      statistic: 'Drop-out',
      text: 'Participants who started the survey (answered at least one question) but exited before reaching the final page.',
    },
  ];

  const calculatePageAverages = useCallback(
    (rawTimings: RawTiming[]): PageTimingData[] => {
      const pageGroups: Record<number, number[]> = {};

      rawTimings.forEach(timing => {
        if (!pageGroups[timing.pageNo]) {
          pageGroups[timing.pageNo] = [];
        }
        pageGroups[timing.pageNo].push(timing.time);
      });

      return Object.entries(pageGroups)
        .map(([pageNo, times]) => {
          const sum = times.reduce((total, time) => total + time, 0);
          return {
            pageNo: parseInt(pageNo, 10),
            averageTimeMs: sum / times.length,
            totalResponses: times.length,
          };
        })
        .sort((a, b) => a.pageNo - b.pageNo);
    },
    [],
  );

  const calculateRespondentTotals = useCallback(
    (rawTimings: RawTiming[]): RespondentTimingData[] => {
      const respondentGroups: Record<number, RawTiming[]> = {};

      rawTimings.forEach(timing => {
        if (!respondentGroups[timing.resultId]) {
          respondentGroups[timing.resultId] = [];
        }
        respondentGroups[timing.resultId].push(timing);
      });

      return Object.entries(respondentGroups)
        .map(([resultId, timings]) => {
          const totalTimeMs = timings.reduce(
            (total, timing) => total + timing.time,
            0,
          );
          return {
            resultId: parseInt(resultId, 10),
            totalTimeMs,
            pageCount: timings.length,
          };
        })
        .sort((a, b) => b.totalTimeMs - a.totalTimeMs);
    },
    [],
  );

  const handleSelectSurveyChange = useCallback(
    (option: any) => {
      console.log('Selected option:', option);
      const value = !Number.isNaN(option.value) ? option?.value : 0;
      setSurveyId(value);

      if (value) {
        setLoading(true);

        api
          .post(`/results/survey/resultsWithProgress`, { surveyId: value })
          .then((response: any) => {
            const stats: any[] = [
              {
                name: 'Number of Links',
                count: response.data.total,
              },
              { name: 'Started', count: response.data.started },
              { name: '≥ 30%', count: response.data.completed },
              {
                name: '< 30%',
                count: response.data.notCompleted,
              },
              {
                name: 'Drop-out',
                count: response.data.dropOut,
              },
            ];
            setTotalResults(response.data.total);
            setStatistics(stats);
            setDropOutData(response.data.dropOutList);
            setDropOutResults(response.data.dropOutResults);
            setTotalPagesInSurvey(response.data.totalPagesInSurvey);
            setSurveyAverageTime(response.data.surveyCompletionTimingAvarge);
            setMaxPageNumberAnsweredFinishedSurvey(
              response.data.maxCurrentPageNoAnseredSurveyFinished,
            );
            setTotalPeopleFinishedSurveyByLastPageNumber(
              response.data.totalPeopleFinishedSurveyByLastPageNumber,
            );
          });

        api
          .get(`/results/raw-page-timing-data/${value}`)
          .then(response => {
            const data = response.data as TimingResponse;
            setTimingResponse(data);
            const pageAverages = calculatePageAverages(data.rawTimings);
            setPageTimingData(pageAverages);
            const respondentTotals = calculateRespondentTotals(data.rawTimings);
            setRespondentTimingData(respondentTotals);
          })
          .catch(error => {
            console.error('Error loading page timing data:', error);
            setPageTimingData([]);
            setRespondentTimingData([]);
            setTimingResponse(null);
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        setPageTimingData([]);
        setRespondentTimingData([]);
        setTimingResponse(null);
      }
    },
    [calculatePageAverages, calculateRespondentTotals],
  );

  const formatTime = (ms: number): string => {
    if (!ms) return '0s';
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${seconds}s`;
  };

  useEffect(() => {
    if (
      !hasAutoSelectedSurvey.current &&
      state?.idSurvey &&
      surveys.length > 0
    ) {
      hasAutoSelectedSurvey.current = true;

      const found = surveys.find(s => s.surveyId === Number(state.idSurvey));

      if (found) {
        setSurveyId(found.surveyId);
        handleSelectSurveyChange({ value: found.surveyId });
      }
    }
  }, [surveys, state?.idSurvey, handleSelectSurveyChange]);

  useEffect(() => {
    if (projectId) {
      setLoading(true);
      api
        .get(`/surveys/project/${projectId}`)
        .then(response => {
          setSurveys(response.data);
        })
        .catch(error => {
          console.error('Error loading surveys:', error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [projectId]);

  return (
    <>
      <h1>Page Timing Analytics</h1>

      <DashboardContainer>
        <DashboardFilterContent>
          <DashoboardFilterBox>
            <Form ref={formRef} onSubmit={() => {}}>
              <div>
                <p>Select Survey</p>
                <select
                  value={surveyId || ''}
                  onChange={e =>
                    handleSelectSurveyChange({
                      value: parseInt(e.target.value, 10),
                    })
                  }
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '8px',
                    marginBottom: '10px',
                  }}
                >
                  <option value="">Choose a survey...</option>
                  {surveys.map(survey => (
                    <option key={survey.surveyId} value={survey.surveyId}>
                      {survey.title} - {survey.surveyId}
                    </option>
                  ))}
                </select>
              </div>
            </Form>
          </DashoboardFilterBox>
        </DashboardFilterContent>

        {!loading ? (
          <>
            {surveyId !== 0 && (
              <>
                <SummaryBox>
                  <div
                    style={{
                      flex: 1,
                      flexDirection: 'column',
                      display: 'flex',
                    }}
                  >
                    <HeaderTitle>
                      <p>Survey Time Summary</p>
                    </HeaderTitle>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-around',
                        margin: '20px 0',
                      }}
                    >
                      <div style={{ textAlign: 'center' }}>
                        <h3>SurveyId</h3>
                        <p style={{ fontSize: '24px', fontWeight: 'bold' }}>
                          {surveyId}
                        </p>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <h3>Average Time to Complete Survey</h3>
                        <p style={{ fontSize: '24px', fontWeight: 'bold' }}>
                          {formatTime(surveyAverageTime)}
                        </p>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <h3>Total Respondents</h3>
                        <p style={{ fontSize: '24px', fontWeight: 'bold' }}>
                          {totalResults || 0}
                        </p>
                      </div>
                    </div>
                  </div>
                </SummaryBox>

                <DashBoardRespondentStatistics>
                  <DashboardChartBox>
                    <div
                      style={{
                        flex: 1,
                        flexDirection: 'column',
                        display: 'flex',
                      }}
                    >
                      <HeaderTitle>
                        <p>Overall Participant Statistics</p>
                      </HeaderTitle>
                      <div style={{ maxHeight: '400px', overflowY: 'visible' }}>
                        <table className="styled-table">
                          <thead>
                            <tr>
                              <th>Statistic</th>
                              <th>Count</th>
                            </tr>
                          </thead>
                          <tbody>
                            {statistics.map((stat, index) => {
                              const tip = tooltips.find(
                                t => t.statistic === stat.name,
                              );
                              return (
                                <tr key={index}>
                                  <td
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 8,
                                    }}
                                  >
                                    {stat.name}
                                    {tip && (
                                      <HelpIcon>
                                        ?
                                        <span className="tooltip">
                                          {tip.text}
                                        </span>
                                      </HelpIcon>
                                    )}
                                  </td>
                                  <td>{stat.count}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </DashboardChartBox>
                  <DashboardChartBox>
                    <div
                      style={{
                        flex: 1,
                        flexDirection: 'column',
                        display: 'flex',
                      }}
                    >
                      <HeaderTitle>
                        <p>Survey Completion Analysis</p>
                      </HeaderTitle>
                      <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        <table className="styled-table">
                          <thead>
                            <tr>
                              <th>
                                {`Last Completed Page Number`}
                                <br />{' '}
                                <span
                                  style={{ fontSize: 12, fontStyle: 'italic' }}
                                >{`Pages where "Finish" button was clicked`}</span>
                              </th>
                              <th>Count</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(
                              totalPeopleFinishedSurveyByLastPageNumber,
                            ).map(([pageLabel, count]) => (
                              <tr key={pageLabel}>
                                <td>{pageLabel}</td>
                                <td>{count}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </DashboardChartBox>

                  <DashboardChartBox>
                    <div
                      style={{
                        flex: 1,
                        flexDirection: 'column',
                        display: 'flex',
                      }}
                    >
                      <HeaderTitle>
                        <p>Drop-out Analysis</p>
                      </HeaderTitle>
                      <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        <table className="styled-table">
                          <thead>
                            <tr>
                              <th>Last Completed Page</th>
                              <th>Count</th>
                            </tr>
                          </thead>
                          <tbody>
                            {dropOutData.map((drop, index) => (
                              <tr key={index}>
                                <td>{drop.page}</td>
                                <td>{drop.count}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </DashboardChartBox>
                </DashBoardRespondentStatistics>

                <DashBoardChartsContent>
                  <DashboardChartBox>
                    <HeaderTitle>
                      <p>Drop-out Count Per Page</p>
                    </HeaderTitle>
                    <ResponsiveContainer width="100%" height={400}>
                      <BarChart
                        data={dropOutData}
                        margin={{
                          top: 20,
                          right: 30,
                          left: 20,
                          bottom: 60,
                        }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="page"
                          label={{
                            value: 'Page Number',
                            position: 'insideBottom',
                            offset: -10,
                          }}
                        />
                        <YAxis
                          label={{
                            value: 'Count',
                            angle: -90,
                            position: 'insideLeft',
                          }}
                          // tickFormatter={value => `${Math.floor(value / 1000)}s`}
                        />
                        <Tooltip labelFormatter={label => `Page ${label}`} />
                        <Legend
                          verticalAlign="bottom"
                          align="center"
                          height={36}
                          wrapperStyle={{
                            bottom: 0,
                            paddingTop: 20,
                            paddingBottom: 10,
                          }}
                        />
                        <Bar
                          name="Count Drop-out"
                          dataKey="count"
                          fill="#8884d8"
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </DashboardChartBox>
                </DashBoardChartsContent>

                <DashBoardChartsContent>
                  <DashboardChartBox>
                    <div
                      style={{
                        flex: 1,
                        flexDirection: 'column',
                        display: 'flex',
                      }}
                    >
                      <HeaderTitle>
                        <p>Drop-out Raw Data</p>
                      </HeaderTitle>
                      <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        <table className="styled-table">
                          <thead>
                            <tr>
                              <th>Respondent ID</th>
                              <th>Last Page Answered Number</th>
                              <th>Progress</th>
                            </tr>
                          </thead>
                          <tbody>
                            {dropOutResults
                              ?.sort(
                                (a, b) =>
                                  a.lastPageAnswered - b.lastPageAnswered,
                              )
                              ?.map((item, index) => (
                                <tr key={`raw-${index}`}>
                                  <td>{item.resultId}</td>
                                  <td>{item.lastPageAnswered}</td>
                                  <td>{item.progressNumber}</td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </DashboardChartBox>
                </DashBoardChartsContent>
              </>
            )}
            {surveyId !== 0 && pageTimingData.length > 0 && (
              <>
                <DashBoardChartsContent>
                  <DashboardChartBox>
                    <HeaderTitle>
                      <p>Average Time Spent Per Page</p>
                    </HeaderTitle>
                    <ResponsiveContainer width="100%" height={400}>
                      <BarChart
                        data={pageTimingData}
                        margin={{
                          top: 20,
                          right: 30,
                          left: 20,
                          bottom: 60,
                        }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="pageNo"
                          label={{
                            value: 'Page Number',
                            position: 'insideBottom',
                            offset: -10,
                          }}
                        />
                        <YAxis
                          label={{
                            value: 'Time (seconds)',
                            angle: -90,
                            position: 'insideLeft',
                          }}
                          tickFormatter={value =>
                            `${Math.floor(value / 1000)}s`
                          }
                        />
                        <Tooltip
                          formatter={(value: any) => [
                            `${formatTime(value)}`,
                            'Average Time',
                          ]}
                          labelFormatter={label => `Page ${label}`}
                        />
                        <Legend
                          verticalAlign="bottom"
                          align="center"
                          height={36}
                          wrapperStyle={{
                            bottom: 0,
                            paddingTop: 20,
                            paddingBottom: 10,
                          }}
                        />
                        <Bar
                          name="Average Time"
                          dataKey="averageTimeMs"
                          fill="#8884d8"
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </DashboardChartBox>
                </DashBoardChartsContent>

                <DashBoardChartsContent>
                  <DashboardChartBox>
                    <div
                      style={{
                        flex: 1,
                        flexDirection: 'column',
                        display: 'flex',
                      }}
                    >
                      <HeaderTitle>
                        <p>Raw Timing Data</p>
                      </HeaderTitle>
                      <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        <table className="styled-table">
                          <thead>
                            <tr>
                              <th>Respondent ID</th>
                              <th>Page Number</th>
                              <th>Time Spent</th>
                            </tr>
                          </thead>
                          <tbody>
                            {timingResponse?.rawTimings.map((item, index) => (
                              <tr
                                key={`raw-${item.resultId}-${item.pageNo}-${index}`}
                              >
                                <td>{item.resultId}</td>
                                <td>{item.pageNo}</td>
                                <td>{formatTime(item.time)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </DashboardChartBox>
                </DashBoardChartsContent>

                <DashBoardChartsContent>
                  <DashboardChartBox>
                    <HeaderTitle>
                      <p>Page Timing Details</p>
                    </HeaderTitle>
                    <table className="styled-table">
                      <thead>
                        <tr>
                          <th>Page Number</th>
                          <th>Average Time</th>
                          <th>Total Responses</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pageTimingData.map(item => (
                          <tr key={item.pageNo}>
                            <td>Page {item.pageNo}</td>
                            <td>{formatTime(item.averageTimeMs ?? 0)}</td>
                            <td>{item.totalResponses}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </DashboardChartBox>
                </DashBoardChartsContent>

                <DashBoardChartsContent>
                  <DashboardChartBox>
                    <HeaderTitle>
                      <p>Time Per Respondent</p>
                    </HeaderTitle>
                    <table className="styled-table">
                      <thead>
                        <tr>
                          <th>Respondent ID</th>
                          <th>Total Time Spent</th>
                          <th>Pages Completed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {respondentTimingData.map(item => (
                          <tr key={item.resultId}>
                            <td>{item.resultId}</td>
                            <td>{formatTime(item.totalTimeMs)}</td>
                            <td>{item.pageCount}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </DashboardChartBox>
                </DashBoardChartsContent>
              </>
            )}
            {surveyId === 0 && pageTimingData.length < 0 ? (
              <ContainerEmpty>
                {'No timing data available for this survey.'}
              </ContainerEmpty>
            ) : (
              <ContainerEmpty>
                {'Please select a survey to view page timing analytics.'}
              </ContainerEmpty>
            )}
          </>
        ) : (
          <ContainerEmpty>
            <>
              <div className="loading-spinner"></div>
              <p>Loading data...</p>
            </>
          </ContainerEmpty>
        )}
      </DashboardContainer>
    </>
  );
};

export default PageTimingAnalytics;
